import { Component } from "@theme/component";
import { StandardEvents, CartLinesUpdateEvent } from "@shopify/events";
import { DrawerOpenEvent, DrawerCloseEvent } from "@theme/theme-drawer";

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ["liveRegion"];

  /** @type {HTMLElement | null} */
  #accountEl = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#accountEl = this.querySelector("shopify-account");
    this.#accountEl?.addEventListener("open", this.#onAccountOpen);
    this.#accountEl?.addEventListener("close", this.#onAccountClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#accountEl?.removeEventListener("open", this.#onAccountOpen);
    this.#accountEl?.removeEventListener("close", this.#onAccountClose);
    this.#unbindAccountPosition();
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute("aria-expanded", cartDrawer.hasAttribute("open") ? "true" : "false");
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== "cart-drawer") return;
    this.#syncCartTriggerAriaExpanded();
  };

  #positionAccountDialog = () => {
    if (!this.#accountEl) return;

    if (window.matchMedia("(max-width: 749px)").matches) {
      this.#accountEl.style.removeProperty("--account-dialog-left");
      return;
    }

    const rect = this.#accountEl.getBoundingClientRect();
    const center = Math.round(rect.left + rect.width / 2);
    this.#accountEl.style.setProperty('--account-dialog-left', `${center}px`);
  };

  #bindAccountPosition = () => {
    window.addEventListener("resize", this.#positionAccountDialog);
    window.addEventListener("scroll", this.#positionAccountDialog, true);
  };

  #unbindAccountPosition = () => {
    window.removeEventListener("resize", this.#positionAccountDialog);
    window.removeEventListener("scroll", this.#positionAccountDialog, true);
  };

  #onAccountOpen = () => {
    this.#positionAccountDialog();
    this.#bindAccountPosition();
  };

  #onAccountClose = () => {
    this.#unbindAccountPosition();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== "AbortError") console.warn("[header-actions] Event promise rejected:", error);
      });
  };
}

if (!customElements.get("header-actions")) {
  customElements.define("header-actions", HeaderActions);
}
